import isEqual from 'fast-deep-equal';
import {map, merge, startWith} from 'rxjs';
import {v4 as uuid} from 'uuid';

import type {
  ClientResponse,
  Conversation,
  ConversationActionParams,
  ConversationArticle,
  RequestAction,
  SelectOptionAction,
  SendConversationMessageParams,
  TriggerParams,
} from '@stripe-internal/conversations-client';
import type {ConversationsApiClient} from '@stripe-internal/conversations-api-client';
import type {InlineDataEventBus} from '@stripe-internal/inline-data';

import type {AppFeatureFlags} from '../../core/types';
import {shouldConversationSendPageContext} from '../../core/lib/util/conversationUtils';

export type MessageHandlerOptions = {
  conversation: Conversation;
  bot: Pick<
    ConversationsApiClient,
    'waitForPendingMessagesHandled' | 'busyObservable' | 'isPending'
  >;
  getFeatureFlags: () => AppFeatureFlags;
  isChat: () => boolean;
  inlineDataEventBus: InlineDataEventBus;
  requestAction: (action: RequestAction) => Promise<ClientResponse>;
};

/**
 * Message and action behavior carried over from the legacy
 * SupportConversationsClient so that Threads keeps parity with the assistant.
 * It sits between the Conversation model and the transport and handles:
 *
 * - gating smart-suggestion requests on tab focus, pending bot work, and
 *   in-progress inline data;
 * - intercepting the classic channel-selection options (`channel_rac`,
 *   `channel_email`, `channel_chat`, `channel_restart`) and writing
 *   `intent` / `requestedChannel` into context instead of dispatching them;
 * - topic / suggested-question / article-visited context and analytics;
 * - enriching outgoing bot messages and actions with page context when the
 *   trigger point asks for it, and marking the prompt a free-text reply
 *   answered;
 * - trigger-query carry-over (waiting for pending messages, interrupting
 *   active inline data) and the `stripe internal test` synthetic messages;
 * - restoring input modes after a failed bot request, the stuck-conversation
 *   warning, and the busy state used to disable input.
 *
 * None of this is Threads-specific; it exists because the bot backend still
 * relies on the client for these flows, and it should shrink as they move
 * server-side. ThreadsConversationClient owns one instance per conversation;
 * chat-routed sends bypass everything except the synthetic test handling.
 */
export class ThreadsMessageHandler {
  private nextTriggerQuery?: string;

  private disposed = false;

  private options: MessageHandlerOptions;

  constructor(options: MessageHandlerOptions) {
    this.options = options;
  }

  dispose() {
    this.disposed = true;
  }

  private get conversation() {
    return this.options.conversation;
  }

  private get latestItem() {
    return this.conversation.log.get().at(-1);
  }

  private get activeInlineData() {
    const item = this.latestItem;
    return item?.name === 'conversation-message-added' &&
      item.message.type === 'inline-data-conversation-message' &&
      !item.message.inlineData.isDone
      ? item.message.inlineData
      : undefined;
  }

  private async addPageContext(data?: Record<string, unknown>) {
    if (!shouldConversationSendPageContext(this.conversation)) {
      return data;
    }
    const response = await this.options.requestAction({
      name: 'request-action',
      action: {name: 'get-page-context'},
      waitForResponse: true,
    });
    return response.data
      ? {...data, ...(response.data as Record<string, unknown>)}
      : data;
  }

  private handleChannelSelection(
    channel: string,
    source: 'button-option' | 'inline-data',
  ): boolean {
    const flags = this.options.getFeatureFlags();
    const inline =
      flags.universalHelpUseNewDesign &&
      flags.universalHelpUseInlineRacEmailForm;
    this.conversation.client.analytics.track('channel_selected', {
      channel:
        source === 'button-option' ? channel.replace(/^channel_/, '') : channel,
    });
    if (channel === 'channel_rac' || channel === 'channel_email') {
      this.conversation.setContextValues({
        intent: 'contact_support',
        ...(!inline
          ? {
              requestedChannel:
                channel === 'channel_rac' ? 'request_a_call' : 'email',
            }
          : {}),
      });
      return true;
    }
    if (source === 'button-option' && channel === 'channel_chat') {
      this.conversation.setContextValues({
        intent: 'contact_support',
        requestedChannel: 'chat',
      });
    }
    if (source === 'button-option' && channel === 'channel_restart') {
      this.conversation.clearContextValues(['intent', 'requestedChannel']);
    }
    return false;
  }

  private handleOptionSelection(action: SelectOptionAction): boolean {
    const model = this.conversation;
    const {message, selectedOptionId} = action;
    if (
      message.purpose === 'channel_selection' &&
      this.handleChannelSelection(selectedOptionId, 'button-option')
    ) {
      return true;
    }
    if (message.purpose === 'topic_selection') {
      const topic = selectedOptionId.replace(/^topic_/, '');
      model.client.analytics.track('topic_selected', {topic});
      model.setContextValue('topic', topic);
    }
    if (selectedOptionId.includes('question_')) {
      model.client.analytics.track('suggested_question_selected', {
        question: message.options.find(
          (option) => option.id === selectedOptionId,
        )?.label,
        optionId: selectedOptionId,
      });
    }
    return false;
  }

  private recordArticleVisit(article: ConversationArticle): void {
    const model = this.conversation;
    const previous = model.context.universalHelp as
      | {articlesVisited?: Record<string, ConversationArticle>}
      | undefined;
    model.setContextValue('universalHelp', {
      ...previous,
      articlesVisited: {...previous?.articlesVisited, [article.id]: article},
    });
  }

  async handleAction(
    params: ConversationActionParams,
    dispatch: (request: ConversationActionParams) => Promise<ClientResponse>,
  ): Promise<ClientResponse> {
    const {action} = params;
    const model = this.conversation;
    if (action.name === 'get-smart-suggestions') {
      if (
        !model.isTabMostRecent() ||
        this.options.bot.isPending(this.conversation.id) ||
        this.activeInlineData
      ) {
        return {code: 205};
      }
      const item = this.latestItem;
      if (
        item?.name === 'conversation-message-added' &&
        item.message.type === 'button-options-conversation-message'
      ) {
        return this.handleAction(
          {
            ...params,
            action: {
              name: 'select-option',
              message: item.message,
              selectedOptionId: action.name,
              data: action.data,
            },
          },
          dispatch,
        );
      }
    }
    if (
      action.name === 'select-option' &&
      this.handleOptionSelection(action as SelectOptionAction)
    ) {
      return {code: 205};
    }
    if (action.name === 'article-visited') {
      this.recordArticleVisit(action.data as ConversationArticle);
    }
    if (action.name === 'inline-data-result' && !model.isTabMostRecent()) {
      model.client.analytics.track('inactive_tab_inline_data_action_ignored');
      return {code: 205};
    }
    if (action.name === 'inline-data-action') {
      const data = action.data as
        | {purpose?: string; channelID?: string}
        | undefined;
      if (
        data?.purpose === 'channel_selection' &&
        data.channelID &&
        this.handleChannelSelection(data.channelID, 'inline-data')
      ) {
        return {code: 205};
      }
    }
    if (action.name === 'handle-trigger-params') {
      const trigger = action.triggerParams as TriggerParams;
      if (trigger.initialQuery || trigger.initialAttachment) {
        return this.handleTrigger(trigger);
      }
    }
    const enriched = {
      ...action,
      data: await this.addPageContext(
        action.data as Record<string, unknown> | undefined,
      ),
    };
    if (this.disposed) {
      return {code: 205};
    }
    const inputModes = model.context.enabledInputModes;
    const response = await dispatch({...params, action: enriched});
    if (this.disposed) {
      return response;
    }
    if (
      this.restoreInputModes(response, inputModes) &&
      action.name === 'select-option' &&
      action.selectedOptionId === 'channel_chat'
    ) {
      model.clearContextValues(['intent', 'requestedChannel']);
    }
    this.checkForStuckConversation(response);
    return response;
  }

  private async handleTrigger({
    initialQuery,
    initialAttachment,
  }: TriggerParams): Promise<ClientResponse> {
    if (this.nextTriggerQuery) {
      this.nextTriggerQuery = initialQuery;
      return {code: 205};
    }
    this.nextTriggerQuery = initialQuery;
    await this.options.bot.waitForPendingMessagesHandled(this.conversation.id);
    if (this.disposed) {
      return {code: 205};
    }
    if (this.activeInlineData?.name === 'live_answers_response') {
      const result = await this.options.inlineDataEventBus.emitWithResult(
        {
          name: 'inline-data-interruption',
          nonce: uuid(),
          data: {conversationId: this.conversation.id},
        },
        {timeoutMs: 5_000},
      );
      if (this.disposed) {
        return {code: 205};
      }
      if (result.error === 'timeout') {
        this.conversation.client.errors.reportWarning(
          'Timed out attempting to interrupt inline data',
        );
      }
    }
    const query = this.nextTriggerQuery;
    if (!query && !initialAttachment) {
      return {code: 205};
    }
    const model = this.conversation;
    const response = await model.sendMessage(
      initialAttachment
        ? model.buildTextWithAttachmentsMessage(query ?? '', [
            initialAttachment,
          ])
        : model.buildTextMessage(query ?? ''),
    );
    this.nextTriggerQuery = undefined;
    return response;
  }

  private handleInternalTestMessage(
    message: Extract<
      SendConversationMessageParams['message'],
      {
        type:
          | 'text-conversation-message'
          | 'text-with-attachments-conversation-message';
      }
    >,
  ): Promise<ClientResponse> | undefined {
    const model = this.conversation;
    const dev = /^\s*stripe\s+internal\s+test\s*$/i.test(message.text);
    const synthetic = /^\s*stripe\s+internal\s+synthetic\s+test\s*$/i.test(
      message.text,
    );
    const endChat =
      /^\s*stripe\s+internal\s+synthetic\s+test\s*-\s*end\s+chat\s*$/i.test(
        message.text,
      );
    if (dev || synthetic || endChat) {
      model.setContextValues(
        dev
          ? {dev: true}
          : {synthetic: true, ...(endChat ? {syntheticEndChat: true} : {})},
      );
      const testId = 'ignore-internal-test-message';
      return model
        .handleEventFromClient({
          id: uuid(),
          timestamp: Date.now(),
          name: 'conversation-message-updated',
          conversationId: model.id,
          participantId: message.participant.id,
          messageId: testId,
          nonce: message.nonce,
          message: {...message, id: testId},
        })
        .then(() => {
          setTimeout(() => {
            if (!this.disposed) {
              model.log.set(
                model.log.get().filter((item) => item.id !== message.id),
              );
            }
          }, 1);
          return {code: 200};
        });
    }
    return undefined;
  }

  async sendMessage(
    params: SendConversationMessageParams,
    dispatch: (
      request: SendConversationMessageParams,
    ) => Promise<ClientResponse>,
  ): Promise<ClientResponse> {
    const {message} = params;
    const model = this.conversation;
    if (
      message.type === 'text-conversation-message' ||
      message.type === 'text-with-attachments-conversation-message'
    ) {
      if (
        !model.context.dev &&
        !model.context.synthetic &&
        !model.context.syntheticEndChat
      ) {
        model.setContextValue('latestQuestion', message.text);
      }
      const internalTestResponse = this.handleInternalTestMessage(message);
      if (internalTestResponse) {
        return internalTestResponse;
      }
    }
    if (this.options.isChat()) {
      return dispatch(params);
    }
    message.data = await this.addPageContext(message.data);
    if (this.disposed) {
      return {code: 205};
    }
    if (
      message.type === 'text-conversation-message' ||
      message.type === 'text-with-attachments-conversation-message'
    ) {
      const log = model.log.get();
      let item = log.at(-1);
      if (
        item?.name === 'conversation-message-added' &&
        item.message.id === message.id
      ) {
        item = log.at(-2);
      }
      if (
        item?.name === 'conversation-message-added' &&
        item.message.type === 'button-options-conversation-message'
      ) {
        const prompt = item.message;
        await model.handleEventFromClient({
          id: uuid(),
          timestamp: Date.now(),
          name: 'conversation-message-updated',
          conversationId: model.id,
          messageId: prompt.id,
          participantId: prompt.participant.id,
          message: {
            ...prompt,
            selectedOptionId:
              message.type === 'text-with-attachments-conversation-message'
                ? 'text-with-attachments-input'
                : 'text-input',
          },
        });
      }
    }
    const inputModes = model.context.enabledInputModes;
    const response = await dispatch(params);
    if (!this.disposed) {
      this.restoreInputModes(response, inputModes);
      this.checkForStuckConversation(response);
    }
    return response;
  }

  private restoreInputModes(
    response: ClientResponse,
    previous: unknown,
  ): boolean {
    if (
      response.code >= 400 &&
      previous &&
      !isEqual(this.conversation.context.enabledInputModes, previous)
    ) {
      this.conversation.setContextValue('enabledInputModes', previous);
      return true;
    }
    return false;
  }

  private checkForStuckConversation(response: ClientResponse) {
    const modes = (this.conversation.context.enabledInputModes ??
      []) as string[];
    const prompt = this.conversation.log
      .get()
      .some(
        (item) =>
          item.name === 'conversation-message-added' &&
          (item.message.type === 'button-options-conversation-message' ||
            item.message.type === 'select-options-conversation-message') &&
          item.message.selectedOptionId === undefined,
      );
    if (response.code === 400 && !modes.includes('text') && !prompt) {
      this.conversation.client.errors.reportWarning(
        'Conversation looks stuck',
        {extras: {conversation_id: this.conversation.id}},
      );
    }
  }

  private isStreaming() {
    if (this.activeInlineData?.name === 'stripe_assistant') {
      return true;
    }
    const item = this.latestItem;
    if (
      item?.name !== 'conversation-message-added' ||
      item.message.type !== 'custom-part-conversation-message'
    ) {
      return false;
    }
    const {part} = item.message;
    return (
      part.type === 'progress' ||
      (part.type === 'ai_response' &&
        (part.dynamic === 'if_latest' || part.dynamic === 'active'))
    );
  }

  isBusy() {
    return (
      !!this.options.getFeatureFlags().enableUhPendingInput &&
      !this.options.isChat() &&
      (this.options.bot.isPending(this.conversation.id) || this.isStreaming())
    );
  }

  busyObservable() {
    return merge(
      this.options.bot.busyObservable(this.conversation.id),
      this.conversation.log.observable(),
    ).pipe(
      startWith(undefined),
      map(() => this.isBusy()),
    );
  }
}
